import React from "react";
import { motion } from "framer-motion";
import Avatar from "../Avatar";

const LABEL = {
    add_task: "Task",
    add_habit: "Habit",
    add_reminder: "Reminder",
    add_list_item: "List item",
    remove_task: "Task",
    remove_habit: "Habit",
    remove_reminder: "Reminder",
    remove_list_item: "List item",
    edit_task: "Task",
    edit_habit: "Habit",
    edit_reminder: "Reminder",
    edit_list_item: "List item",
    complete_task: "Task",
    complete_habit: "Habit",
    complete_reminder: "Reminder",
    complete_list_item: "List item",
    easter_egg: "Easter egg",
    safety: "Mochi here",
    summon_celebration: "Boost",
};

// Maps an action type to its chip tone/verb/glyph.
function chipPresentation(t) {
    if (t === "easter_egg") return { tone: "bg-mustard text-warm-900", verb: "unlocked", glyph: "🎀" };
    if (t === "summon_celebration") return { tone: "bg-mustard text-warm-900", verb: "incoming", glyph: "🎉" };
    if (t === "safety") return { tone: "bg-sage-soft text-warm-900", verb: "with you", glyph: "🍡" };
    if (t.startsWith("complete_")) return { tone: "bg-sage", verb: "completed", glyph: "✓" };
    if (t.startsWith("remove_")) return { tone: "bg-terracotta", verb: "removed", glyph: "✕" };
    if (t.startsWith("edit_")) return { tone: "bg-mustard text-warm-900", verb: "updated", glyph: "✎" };
    return { tone: "bg-sage", verb: "added", glyph: "✦" };
}

// A single chat message bubble with optional action chips.
export function ChatBubble({ role, text, actions, avatarStyle, avatarSkin, mood }) {
    const isUser = role === "user";
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-end gap-2 ${isUser ? "justify-end" : "justify-start"}`}
        >
            {!isUser && (
                <div className="w-7 h-7 rounded-full bg-surface flex items-center justify-center shrink-0">
                    <Avatar mood={mood} size={26} style={avatarStyle} skin={avatarSkin} />
                </div>
            )}
            <div
                className={`max-w-[78%] px-3.5 py-2.5 font-body text-sm leading-snug whitespace-pre-wrap break-words
                    ${isUser
                        ? "bg-terracotta text-cream rounded-3xl rounded-br-sm"
                        : "bg-surface text-warm-900 rounded-3xl rounded-bl-sm"
                    }`}
            >
                {text}
                {!isUser && actions && actions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {actions.map((a, i) => {
                            const { tone, verb, glyph } = chipPresentation(a.type || "");
                            return (
                                <span
                                    // Composite key: same action type can appear twice in a
                                    // single message (e.g. two `add_task` chips), and chip
                                    // order can shift if Claude re-orders actions across a
                                    // re-render. Index alone caused chip-state flicker.
                                    key={`${a.type}-${a.title || a.day || a.time || ""}-${i}`}
                                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-display text-cream rounded-full px-2 py-0.5 ${tone}`}
                                    data-testid={`chat-action-${a.type}`}
                                >
                                    {glyph} {LABEL[a.type] || a.type} {verb}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>
        </motion.div>
    );
}

// Typing indicator dots.
export function TypingDots() {
    return (
        <span className="inline-flex gap-1 items-center">
            {[0, 1, 2].map((i) => (
                <motion.span
                    key={i}
                    className="inline-block w-1.5 h-1.5 rounded-full bg-warm-500"
                    animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
                />
            ))}
        </span>
    );
}
